import { useQuery } from '@tanstack/react-query'
import { api, matchesLine, type LineInfo, type StopInfo } from './api'

export const queryKeys = {
  lines: ['lines'] as const,
  routes: (lineCode: string) => ['routes', lineCode] as const,
  routeByLineSlug: (lineSlug: string) => ['routeByLineSlug', lineSlug] as const,
  stops: (routeCode: string) => ['stops', routeCode] as const,
  eta: (routeCode: string, stopCode: string) => ['eta', routeCode, stopCode] as const,
  stopDetails: (stopCode: string) => ['stopDetails', stopCode] as const,
  routesForStop: (stopCode: string) => ['routesForStop', stopCode] as const,
  stopArrivals: (stopCode: string) => ['stopArrivals', stopCode] as const,
  closestStops: (lat: number, lng: number) => ['closestStops', lat, lng] as const,
  busLocations: (routeCode: string) => ['busLocations', routeCode] as const,
}

export function useLines() {
  return useQuery({
    queryKey: queryKeys.lines,
    queryFn: api.getLines,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRoutes(lineCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.routes(lineCode || ''),
    queryFn: () => api.getRoutes(lineCode!),
    enabled: !!lineCode,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRouteByLineSlug(lineSlug: string | null) {
  return useQuery({
    queryKey: queryKeys.routeByLineSlug(lineSlug || ''),
    queryFn: () => api.getFirstRouteForLine(lineSlug!),
    enabled: !!lineSlug,
    staleTime: 5 * 60 * 1000,
  })
}

export function useStops(routeCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.stops(routeCode || ''),
    queryFn: () => api.getStops(routeCode!),
    enabled: !!routeCode,
    staleTime: 5 * 60 * 1000,
  })
}

export function useEta(routeCode: string | undefined, stop: StopInfo | undefined) {
  return useQuery({
    queryKey: queryKeys.eta(routeCode || '', stop?.StopCode || ''),
    queryFn: () => api.getEta(routeCode!, stop!),
    enabled: !!routeCode && !!stop,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  })
}

export function useLineBySlug(lines: LineInfo[] | undefined, lineSlug: string | null) {
  if (!lines || !lineSlug) return null
  return lines.find(l => matchesLine(l, lineSlug)) || null
}

export function useStopByCode(stops: StopInfo[] | undefined, stopCode: string | null) {
  if (!stops || !stopCode) return null
  return stops.find(s => s.StopCode === stopCode) || null
}

export function useStopDetails(stopCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.stopDetails(stopCode || ''),
    queryFn: () => api.getStopDetails(stopCode!),
    enabled: !!stopCode,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRoutesForStop(stopCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.routesForStop(stopCode || ''),
    queryFn: () => api.getRoutesForStop(stopCode!),
    enabled: !!stopCode,
    staleTime: 5 * 60 * 1000,
  })
}

export function useStopArrivals(stopCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.stopArrivals(stopCode || ''),
    queryFn: () => api.getStopArrivals(stopCode!),
    enabled: !!stopCode,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  })
}

export function useClosestStops(lat: number | undefined, lng: number | undefined) {
  return useQuery({
    queryKey: queryKeys.closestStops(lat || 0, lng || 0),
    queryFn: () => api.getClosestStops(lat!, lng!),
    enabled: lat !== undefined && lng !== undefined,
    staleTime: 5 * 60 * 1000,
  })
}

export function useBusLocations(routeCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.busLocations(routeCode || ''),
    queryFn: () => api.getBusLocations(routeCode!),
    enabled: !!routeCode,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  })
}
